import {
    Avatar,
    Box,
    Button,
    Stack,
    TextField,
    Typography,
    Paper,
    useTheme,
    Alert,
    CircularProgress,
    Divider,
} from "@mui/material";
// TODO: Descomentar cuando se agregue la lógica para los botones
// import CallIcon from "@mui/icons-material/Call";
// import ScheduleIcon from "@mui/icons-material/Schedule";
import SendIcon from "@mui/icons-material/Send";
import { useEffect, useState } from "react";
import { useRandomAgent } from "../../../hooks/useRandomAgent";
import { useSubmitContactForm } from "../../../hooks/useContactForm";
import type { Agent } from "../data/propertyData";

interface AgentContactProps {
    agent: Agent;
    propertyId?: string;
    propertyName?: string;
    propertyAddress?: string;
    vendorId?: number;
    embedId?: string;
    agentId?: string | number;
    isDefaultBroker?: boolean;
}

export default function AgentContact({ 
    agent, 
    propertyId = "123", 
    propertyName = "Property", 
    propertyAddress = "",
    vendorId = 298,
    embedId,
    agentId,
    isDefaultBroker = false,
}: AgentContactProps) {
    const theme = useTheme();
    const { data: randomAgentData, isLoading, error } = useRandomAgent(embedId || "ap_5b0e9c7ad4312f86b2c4e9a103d7f5c8_qn5h0z2y", undefined, agentId);
    const submitContactForm = useSubmitContactForm();

    // Estado del formulario
    const [formData, setFormData] = useState({
        name: "",
        email: "",
        phone: "",
        message: "",
    });

    // useEffect para ver los datos que llegan
    useEffect(() => {
        console.log('=== AgentContact useEffect ===');
        console.log('randomAgentData:', randomAgentData);
        console.log('isDefaultBroker:', isDefaultBroker);
        console.log('isLoading:', isLoading);
        console.log('error:', error);
        console.log('randomAgentData?.data:', randomAgentData?.data);
        console.log('Full response:', JSON.stringify(randomAgentData, null, 2));
    }, [randomAgentData, isLoading, error]);

    // Extraer el agente de la respuesta de la API
    const apiAgent = randomAgentData?.data?.data?.[0]?.data;
    
    // Mapear los datos del API al formato esperado, o usar el prop agent como fallback
    const displayAgent: Agent = apiAgent
        ? {
              name: apiAgent.fullName || agent.name,
              phone: apiAgent.phone || agent.phone,
              image: apiAgent.photo || agent.image,
              description: apiAgent.shortDescription || apiAgent.longDescription || agent.description,
          }
        : agent;

    // Obtener email del agente
    const agentEmail = apiAgent?.email || "";

    // Manejar cambios en los campos del formulario
    const handleInputChange = (field: string) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFormData(prev => ({
            ...prev,
            [field]: event.target.value,
        }));
    };

    // Manejar envío del formulario
    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        
        // Validación básica
        if (!formData.name || !formData.email || !formData.phone || !formData.message) {
            return;
        }

        try {
            // Obtener la URL actual de la página
            const currentUrl = typeof window !== 'undefined' ? window.location.href : '';
            
            await submitContactForm.mutateAsync({
                name: formData.name,
                email: formData.email,
                phone: formData.phone,
                message: formData.message,
                vendorId: vendorId,
                locationId: 0,
                email_agent: agentEmail,
                createdBy: "CONTACT_FORM",
                type: "member",
                embedId: embedId,
                url: currentUrl,
                data_property_contact: {
                    propertyId: propertyId,
                    propertyName: propertyName,
                    address: propertyAddress,
                },
            });

            // Limpiar formulario después de envío exitoso
            setFormData({
                name: "",
                email: "",
                phone: "",
                message: "",
            });
        } catch (error) {
            console.error("Error al enviar el formulario:", error);
        }
    };

    return (
        <Paper
            elevation={3}
            sx={{
                borderRadius: 3,
                overflow: "hidden",
                width: "100%",
            }}
        >
            {/* Header Section - Dark Blue */}
            <Box
                sx={{
                    backgroundColor: theme.palette.primary.main,
                    p: 4,
                    textAlign: "center",
                }}
            >
                <Avatar
                    src={displayAgent.image}
                    sx={{
                        width: 120,
                        height: 120,
                        mx: "auto",
                        mb: 2,
                        border: `4px solid ${theme.palette.secondary.main}`,
                    }}
                />
                <Typography
                    variant="h4"
                    fontWeight="bold"
                    sx={{
                        color: "white",
                        mb: 0.5,
                    }}
                >
                    {isDefaultBroker ? displayAgent.name : "Contact Agent"}
                </Typography>
                {isDefaultBroker && (
                    <Typography
                        variant="body2"
                        sx={{
                            color: theme.palette.secondary.main,
                            mb: 2,
                            fontWeight: 600,
                            letterSpacing: 1,
                        }}
                    >
                        {apiAgent?.role ? apiAgent.role.toUpperCase() : "LICENSED AGENT"}
                    </Typography>
                )}
               {/* <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
                    <CallIcon sx={{ color: "white", fontSize: 20 }} />
                    <Typography sx={{ color: "white", fontSize: "1rem" }}>
                        {displayAgent.phone}
                    </Typography>
                </Box>*/}
            </Box>

            {/* Bio Section */}
            <Box sx={{ p: 3, backgroundColor: "background.paper" }}>
                <Typography
                    variant="body2"
                    sx={{
                        color: "text.secondary",
                        lineHeight: 1.7,
                        mb: 3,
                    }}
                >
                    {displayAgent.description}
                </Typography>

                {/* Action Buttons */}
                {/* TODO: Agregar lógica para Schedule Tour y Call Now */}
                {/* <Stack spacing={2}>
                    <Button
                        variant="contained"
                        color="secondary"
                        startIcon={<ScheduleIcon />}
                        fullWidth
                        sx={{
                            py: 1.5,
                            fontWeight: "bold",
                        }}
                    >
                        Schedule Tour
                    </Button>
                    <Button
                        variant="outlined"
                        startIcon={<CallIcon />}
                        fullWidth
                        sx={{
                            py: 1.5,
                            borderColor: theme.palette.primary.main,
                            color: theme.palette.primary.main,
                            fontWeight: "bold",
                            "&:hover": {
                                borderColor: theme.palette.primary.dark,
                                backgroundColor: `${theme.palette.primary.main}10`,
                            },
                        }}
                    >
                        Call Now
                    </Button>
                </Stack> */}
            </Box>
            <Divider sx={{ mb: 3, borderColor: '#f8f4f0', borderBottomWidth: '2px' }} />


            {/* Contact Form */}
            <Box sx={{ p: 3, backgroundColor: "background.paper" }}>
                <form onSubmit={handleSubmit}>
                    <Stack spacing={2.5}>
                        {/* Mensajes de éxito/error */}
                        {submitContactForm.isSuccess && (
                            <Alert severity="success">
                                Message sent successfully! We'll get back to you soon.
                            </Alert>
                        )}
                        {submitContactForm.isError && (
                            <Alert severity="error">
                                {submitContactForm.error instanceof Error 
                                    ? submitContactForm.error.message 
                                    : "Error sending message. Please try again."}
                            </Alert>
                        )}

                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#0e2340",
                                    textTransform: "uppercase",
                                    mb: 0.5,
                                    display: "block",
                                    textAlign: "center",
                                }}
                            >
                                NAME
                            </Typography>
                            <TextField
                                placeholder="Your full name"
                                fullWidth
                                size="small"
                                value={formData.name}
                                onChange={handleInputChange("name")}
                                required
                                disabled={submitContactForm.isPending}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#0e2340",
                                    textTransform: "uppercase",
                                    mb: 0.5,
                                    display: "block",
                                    textAlign: "center",
                                }}
                            >
                                EMAIL
                            </Typography>
                            <TextField
                                placeholder="your@email.com"
                                fullWidth
                                size="small"
                                type="email"
                                value={formData.email}
                                onChange={handleInputChange("email")}
                                required
                                disabled={submitContactForm.isPending}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#0e2340",
                                    textTransform: "uppercase",
                                    mb: 0.5,
                                    display: "block",
                                    textAlign: "center",
                                }}
                            >
                                PHONE
                            </Typography>
                            <TextField
                                placeholder="(555) 123-4567"
                                fullWidth
                                size="small"
                                type="tel"
                                value={formData.phone}
                                onChange={handleInputChange("phone")}
                                required
                                disabled={submitContactForm.isPending}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>
                        <Box>
                            <Typography
                                variant="caption"
                                sx={{
                                    fontWeight: "bold",
                                    color: "#0e2340",
                                    textTransform: "uppercase",
                                    mb: 0.5,
                                    display: "block",
                                    textAlign: "center",
                                }}
                            >
                                MESSAGE
                            </Typography>
                            <TextField
                                placeholder="I'm interested in this property and would like to schedule a showing."
                                fullWidth
                                multiline
                                rows={4}
                                size="small"
                                value={formData.message}
                                onChange={handleInputChange("message")}
                                required
                                disabled={submitContactForm.isPending}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        backgroundColor: "white",
                                    },
                                }}
                            />
                        </Box>
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            startIcon={submitContactForm.isPending ? <CircularProgress size={20} color="inherit" /> : <SendIcon />}
                            fullWidth
                            disabled={submitContactForm.isPending || !formData.name || !formData.email || !formData.phone || !formData.message}
                            sx={{
                                py: 1.5,
                                fontWeight: "bold",
                                mt: 1,
                            }}
                        >
                            {submitContactForm.isPending ? "Sending..." : "Send Message"}
                        </Button>
                    </Stack>
                </form>
            </Box>

            {/* Footer */}
            <Box
                sx={{
                    p: 2,
                    backgroundColor: "#f9f4f1",
                    textAlign: "center",
                }}
            >
                <Typography
                    variant="body2"
                    fontWeight="bold"
                    sx={{
                        color: theme.palette.primary.main,
                        mb: 0.5,
                    }}
                >
                    LAKESIDE WINDERMERE REALTY
                </Typography>
                <Typography
                    variant="caption"
                    sx={{
                        color: theme.palette.secondary.main,
                    }}
                >
                    Your Dream Home Awaits
                </Typography>
            </Box>
        </Paper>
    );
}
